export const organizationConnectionStatuses = [
  "pending",
  "active",
  "rejected",
  "suspended",
  "disconnected",
] as const;

export const organizationConnectionPolicies = [
  "open",
  "approvalRequired",
  "invitationOnly",
  "validated",
] as const;

export const organizationConnectionValidationStatuses = [
  "not_required",
  "missing",
  "failed",
  "passed",
] as const;

export const organizationConnectionInvitationStatuses = [
  "not_required",
  "presented",
  "accepted",
  "rejected",
] as const;

export type OrganizationConnectionStatus = (typeof organizationConnectionStatuses)[number];
export type OrganizationConnectionPolicyId = (typeof organizationConnectionPolicies)[number];
export type OrganizationConnectionValidationStatus =
  (typeof organizationConnectionValidationStatuses)[number];
export type OrganizationConnectionInvitationStatus =
  (typeof organizationConnectionInvitationStatuses)[number];

export type OrganizationConnectionAccess = {
  basicConnectedPerson: boolean;
};

export type OrganizationConnectionInvitationMetadata = {
  invitationId: string | null;
  tokenHash: string | null;
  status: OrganizationConnectionInvitationStatus;
  acceptedAt: unknown | null;
};

export type OrganizationConnectionValidationMetadata = {
  method: string | null;
  status: OrganizationConnectionValidationStatus;
  reference: string | null;
  validatedAt: unknown | null;
};

export type OrganizationConnectionMigrationMetadata = {
  legacyFirebaseUid: string | null;
  legacySource: string | null;
  migratedAt: unknown | null;
};

export type OrganizationConnectionRecord = {
  connectionId: string;
  organizationId: string;
  personId: string;
  uid: string;
  umidSnapshot: string | null;
  status: OrganizationConnectionStatus;
  connectionType: "basic_connected_person";
  access: OrganizationConnectionAccess;
  roles: string[];
  source:
    | "development_seed"
    | "manual_admin_approval"
    | "migration_hvicc"
    | "request_approval_required"
    | "request_invitation"
    | "request_open_auto_accept"
    | "request_validated";
  requestedAt: unknown | null;
  approvedAt: unknown | null;
  rejectedAt: unknown | null;
  suspendedAt: unknown | null;
  disconnectedAt: unknown | null;
  invitation: OrganizationConnectionInvitationMetadata;
  validation: OrganizationConnectionValidationMetadata;
  migration: OrganizationConnectionMigrationMetadata;
  createdAt: unknown;
  updatedAt: unknown;
};

export type OrganizationConnectionProjection = Pick<
  OrganizationConnectionRecord,
  | "access"
  | "approvedAt"
  | "connectionId"
  | "connectionType"
  | "createdAt"
  | "disconnectedAt"
  | "organizationId"
  | "personId"
  | "rejectedAt"
  | "requestedAt"
  | "roles"
  | "source"
  | "status"
  | "suspendedAt"
  | "uid"
  | "umidSnapshot"
  | "updatedAt"
>;

export type OrganizationConnectionInvitation = {
  invitationId: string;
  organizationId: string;
  tokenHash: string;
  status: "active" | "revoked";
  expiresAt: unknown;
  maxUses: number;
  uses: number;
  intendedEmailNormalized: string | null;
  createdAt: unknown;
  revokedAt: unknown | null;
};

export type OrganizationConnectionValidationSettings = {
  testCode: string | null;
};

export type OrganizationConnectionValidationResult = {
  accepted: boolean;
  method: string | null;
  reference: string | null;
  status: OrganizationConnectionValidationStatus;
};

export type OrganizationConnectionValidationInput = {
  method?: string;
  value?: string;
};

export type OrganizationConnectionValidation = {
  valid: boolean;
  errors: string[];
};

export function buildOrganizationConnectionId(personId: string, organizationId: string): string {
  return `${personId}_${organizationId}`;
}

export function organizationConnectionDocumentPath(
  personId: string,
  organizationId: string,
): string {
  return `organizationConnections/${personId}/organizations/${organizationId}`;
}

export function organizationConnectionProjectionPath(
  organizationId: string,
  personId: string,
): string {
  return `organizations/${organizationId}/connectionIndex/${personId}`;
}

export function buildBasicOrganizationConnectionRecord(input: {
  organizationId: string;
  personId: string;
  roles?: string[];
  source: OrganizationConnectionRecord["source"];
  status: OrganizationConnectionStatus;
  timestamp: unknown;
  umidSnapshot?: string | null;
  invitation?: Partial<OrganizationConnectionInvitationMetadata>;
  validation?: Partial<OrganizationConnectionValidationMetadata>;
  migration?: Partial<OrganizationConnectionMigrationMetadata>;
}): OrganizationConnectionRecord {
  const active = input.status === "active";
  const roles = input.roles ?? (active ? ["connected_person"] : []);

  return {
    access: {
      basicConnectedPerson: active && roles.includes("connected_person"),
    },
    approvedAt: active ? input.timestamp : null,
    connectionId: buildOrganizationConnectionId(input.personId, input.organizationId),
    connectionType: "basic_connected_person",
    createdAt: input.timestamp,
    disconnectedAt: input.status === "disconnected" ? input.timestamp : null,
    invitation: {
      acceptedAt: null,
      invitationId: null,
      status: "not_required",
      tokenHash: null,
      ...input.invitation,
    },
    migration: {
      legacyFirebaseUid: null,
      legacySource: null,
      migratedAt: null,
      ...input.migration,
    },
    organizationId: input.organizationId,
    personId: input.personId,
    rejectedAt: input.status === "rejected" ? input.timestamp : null,
    requestedAt: input.timestamp,
    roles,
    source: input.source,
    status: input.status,
    suspendedAt: input.status === "suspended" ? input.timestamp : null,
    uid: input.personId,
    umidSnapshot: input.umidSnapshot ?? null,
    updatedAt: input.timestamp,
    validation: {
      method: null,
      reference: null,
      status: "not_required",
      validatedAt: null,
      ...input.validation,
    },
  };
}

export function buildOrganizationConnectionProjection(
  connection: OrganizationConnectionRecord,
): OrganizationConnectionProjection {
  return {
    access: connection.access,
    approvedAt: connection.approvedAt,
    connectionId: connection.connectionId,
    connectionType: connection.connectionType,
    createdAt: connection.createdAt,
    disconnectedAt: connection.disconnectedAt,
    organizationId: connection.organizationId,
    personId: connection.personId,
    rejectedAt: connection.rejectedAt,
    requestedAt: connection.requestedAt,
    roles: connection.roles,
    source: connection.source,
    status: connection.status,
    suspendedAt: connection.suspendedAt,
    uid: connection.uid,
    umidSnapshot: connection.umidSnapshot,
    updatedAt: connection.updatedAt,
  };
}

export function canGrantBasicOrganizationAccess(connection: unknown): boolean {
  if (!isRecord(connection)) {
    return false;
  }

  return (
    connection.status === "active" &&
    isRecord(connection.access) &&
    connection.access.basicConnectedPerson === true
  );
}

export function policyFromPublicDirectoryValue(value: unknown): OrganizationConnectionPolicyId {
  return value === "approval_required" ? "approvalRequired" : "open";
}

export function evaluateTestConnectionValidation(
  payload: unknown,
  settings: OrganizationConnectionValidationSettings,
): OrganizationConnectionValidationResult {
  if (!isRecord(payload)) {
    return {
      accepted: false,
      method: null,
      reference: null,
      status: "missing",
    };
  }

  const method = typeof payload.method === "string" ? payload.method : null;
  const value = typeof payload.value === "string" ? payload.value.trim() : "";

  if (!method || !value) {
    return {
      accepted: false,
      method,
      reference: null,
      status: "missing",
    };
  }

  if (method === "testCode" && settings.testCode !== null && value === settings.testCode) {
    return {
      accepted: true,
      method,
      reference: "testCode",
      status: "passed",
    };
  }

  return {
    accepted: false,
    method,
    reference: method,
    status: "failed",
  };
}

export function validateOrganizationConnectionRecord(
  connection: unknown,
): OrganizationConnectionValidation {
  const errors: string[] = [];

  if (!isRecord(connection)) {
    return { errors: ["Connection must be an object."], valid: false };
  }

  const personId = stringValue(connection.personId);
  const organizationId = stringValue(connection.organizationId);
  const connectionId = stringValue(connection.connectionId);

  if (!isFirestoreSafeDocumentId(personId)) {
    errors.push("Connection personId must be a Firestore-safe document ID.");
  }

  if (!isFirestoreSafeDocumentId(organizationId)) {
    errors.push("Connection organizationId must be Firestore-safe.");
  }

  if (connectionId !== buildOrganizationConnectionId(personId, organizationId)) {
    errors.push("Connection ID must be deterministic from personId and organizationId.");
  }

  if (!organizationConnectionStatuses.includes(connection.status as OrganizationConnectionStatus)) {
    errors.push("Connection status must be supported.");
  }

  if (connection.connectionType !== "basic_connected_person") {
    errors.push("Connection type must be basic_connected_person for EXP-005.");
  }

  if (!isRecord(connection.access) || typeof connection.access.basicConnectedPerson !== "boolean") {
    errors.push("Connection access.basicConnectedPerson is required.");
  }

  if (
    !Array.isArray(connection.roles) ||
    !connection.roles.every((role) => typeof role === "string")
  ) {
    errors.push("Connection roles compatibility field must be an array of strings.");
  }

  if (connection.status === "active" && !canGrantBasicOrganizationAccess(connection)) {
    errors.push("Active connection must grant basic connected-person access.");
  }

  if (connection.status !== "active" && canGrantBasicOrganizationAccess(connection)) {
    errors.push("Only active connections may grant basic connected-person access.");
  }

  if ("platformRole" in connection || "admin" in connection || "permissions" in connection) {
    errors.push("Connection must not contain platform/admin/permission shortcuts.");
  }

  return {
    errors,
    valid: errors.length === 0,
  };
}

function isFirestoreSafeDocumentId(value: string): boolean {
  return value.length > 0 && value.length <= 150 && !value.includes("/");
}

function stringValue(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
